import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiChevronDown, FiCreditCard, FiFileText, FiGrid, FiInfo, FiTag, FiUsers } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import { isRoleAllowed, resolveAdminRole, ADMIN_ROUTE_ACCESS } from './roleAdmin';

function formatRoleLabel(role) {
  const labels = {
    super_admin: 'Super Admin',
    admin_keuangan: 'Admin Keuangan',
    admin_program: 'Admin Program',
    customer_service: 'Customer Service',
    pimpinan: 'Pimpinan',
  };

  return labels[role] || 'Admin';
}

function SidebarAdmin({ isOpen = true, children, admin }) {
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState(null);
  const role = resolveAdminRole(admin);
  const canAccess = useCallback(
    (routeKey) => (
      Object.prototype.hasOwnProperty.call(ADMIN_ROUTE_ACCESS, routeKey) &&
      isRoleAllowed(role, ADMIN_ROUTE_ACCESS[routeKey])
    ),
    [role]
  );
  const transactionItems = useMemo(
    () => [
      { key: 'inputTransaksi', to: '/admin/input-transaksi', label: 'Input Transaksi' },
      { key: 'konfirmasiDonasi', to: '/konfirmasi/donasi', label: 'Konfirmasi Donasi' },
      { key: 'laporanDonasi', to: '/laporan/donasi', label: 'Laporan Donasi' },
      { key: 'qrCode', to: '/transaksi/qrcode', label: 'QRIS' },
    ].filter((item) => canAccess(item.key)),
    [canAccess]
  );
  const penyaluranItems = useMemo(
    () => [
      { key: 'listPenyaluran', to: '/penyaluran/list', label: 'List Penyaluran Program' },
      { key: 'inputPenyaluran', to: '/penyaluran/input', label: 'Input Penyaluran Program' },
      { key: 'penerimaManfaat', to: '/penyaluran/penerima-manfaat', label: 'Input Program' },
    ].filter((item) => canAccess(item.key)),
    [canAccess]
  );
  const transactionPaths = useMemo(() => transactionItems.map((item) => item.to), [transactionItems]);
  const penyaluranPaths = useMemo(() => penyaluranItems.map((item) => item.to), [penyaluranItems]);

  

  const toggleDropdown = (key) => {
    setOpenDropdown((prev) => (prev === key ? null : key));
  };

  const isActivePath = useCallback(
    (paths) => paths.some((path) => location.pathname.startsWith(path)),
    [location.pathname]
  );

  useEffect(() => {
    if (location.pathname === '/') {
      setOpenDropdown(null);
      return;
    }

    if (transactionPaths.length && isActivePath(transactionPaths)) {
      setOpenDropdown('transaksi');
      return;
    }

    if (penyaluranPaths.length && isActivePath(penyaluranPaths)) {
      setOpenDropdown('penyaluran');
      return;
    }

    setOpenDropdown(null);
  }, [location.pathname, isActivePath, transactionPaths, penyaluranPaths]);

  const adminRoleLabel = useMemo(() => formatRoleLabel(role), [role]);

  const baseItemClasses =
    'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200';

  const activeItemClasses =
    'bg-blue-900 text-white';

  const idleItemClasses =
    'text-slate-600 hover:bg-slate-100 hover:text-slate-900';

  const iconClasses = (active) =>
    `flex h-8 w-8 items-center justify-center rounded-lg text-[15px] transition-colors ${
      active
        ? 'bg-white/20 text-white'
        : 'text-slate-500 group-hover:text-slate-700'
    }`;

  const subItemClasses = (active) =>
    `block rounded-lg px-3 py-2 text-sm transition-colors ${
      active
        ? 'bg-slate-100 text-slate-900 font-semibold'
        : 'text-slate-500 hover:text-slate-900'
    }`;

  return (
    <>
      <aside
        className={`fixed top-16 bottom-0 left-0 z-30 w-72 transform border-r border-orange-400 bg-white text-slate-700 shadow-sm transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col">

          <nav className="flex-1 overflow-y-auto px-4 py-3">
            <div className="space-y-4">
              <div className="space-y-2">
                <Link
                  to="/"
                  className={`${baseItemClasses} ${
                    location.pathname === '/' ? activeItemClasses : idleItemClasses
                  }`}
                >
                  <span className={iconClasses(location.pathname === '/')}>
                    <FiGrid />
                  </span>
                  <p className="truncate">Dashboard</p>
                </Link>
              </div>

              {canAccess('listDonatur') && (
                <Link
                  to="/list/donatur"
                  className={`${baseItemClasses} ${
                    location.pathname.startsWith('/list/donatur')
                      ? activeItemClasses
                      : idleItemClasses
                  }`}
                >
                  <span className={iconClasses(location.pathname.startsWith('/list/donatur'))}>
                    <FiUsers />
                  </span>
                  <p className="truncate">List Donatur</p>
                </Link>
              )}

              {transactionItems.length > 0 && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggleDropdown('transaksi')}
                  className={`${baseItemClasses} w-full justify-between ${
                    isActivePath(transactionPaths)
                      ? activeItemClasses
                      : idleItemClasses
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={iconClasses(isActivePath(transactionPaths))}>
                      <FiCreditCard />
                    </span>
                    <p className="truncate">Donasi</p>
                  </div>
                  <FiChevronDown
                    className={`text-sm flex-shrink-0 transition-transform ${
                      openDropdown === 'transaksi' ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openDropdown === 'transaksi' && (
                  <div className="ml-8 space-y-1">
                    {transactionItems.map((item) => (
                      <Link
                        key={item.key}
                        to={item.to}
                        className={subItemClasses(location.pathname.startsWith(item.to))}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              )}

              {penyaluranItems.length > 0 && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggleDropdown('penyaluran')}
                  className={`${baseItemClasses} w-full justify-between ${
                    isActivePath(penyaluranPaths)
                      ? activeItemClasses
                      : idleItemClasses
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={iconClasses(isActivePath(penyaluranPaths))}>
                      <FiInfo />
                    </span>
                    <p className="truncate">Penyaluran</p>
                  </div>
                  <FiChevronDown
                    className={`text-sm flex-shrink-0 transition-transform ${
                      openDropdown === 'penyaluran' ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openDropdown === 'penyaluran' && (
                  <div className="ml-8 space-y-1">
                    {penyaluranItems.map((item) => (
                      <Link
                        key={item.key}
                        to={item.to}
                        className={subItemClasses(location.pathname.startsWith(item.to))}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              )}

              {canAccess('pengeluaran') && (
                <Link
                  to="/pengeluaran"
                  className={`${baseItemClasses} ${
                    location.pathname.startsWith('/pengeluaran') && !location.pathname.startsWith('/pengeluaran/jenis-beban')
                      ? activeItemClasses
                      : idleItemClasses
                  }`}
                >
                  <span className={iconClasses(location.pathname.startsWith('/pengeluaran'))}>
                    <FiCreditCard />
                  </span>
                  <p className="truncate">Pengeluaran</p>
                </Link>
              )}

              {canAccess('jenisBeban') && (
                <Link
                  to="/pengeluaran/jenis-beban"
                  className={`${baseItemClasses} ${
                    location.pathname.startsWith('/pengeluaran/jenis-beban')
                      ? activeItemClasses
                      : idleItemClasses
                  }`}
                >
                  <span className={iconClasses(location.pathname.startsWith('/pengeluaran/jenis-beban'))}>
                    <FiTag />
                  </span>
                  <p className="truncate">Jenis Beban</p>
                </Link>
              )}

              {canAccess('pengajuan') && (
                <Link
                  to="/pengajuan/riwayat"
                  className={`${baseItemClasses} ${
                    isActivePath(['/pengajuan/riwayat', '/pengajuan/detail'])
                      ? activeItemClasses
                      : idleItemClasses
                  }`}
                >
                  <span className={iconClasses(isActivePath(['/pengajuan/riwayat', '/pengajuan/detail']))}>
                    <FiFileText />
                  </span>
                  <p className="truncate">Pengajuan</p>
                </Link>
              )}

              {canAccess('manajemenKonten') && (
                <Link
                  to="/manajemen-konten"
                  className={`${baseItemClasses} ${
                    location.pathname.startsWith('/manajemen-konten')
                      ? activeItemClasses
                      : idleItemClasses
                  }`}
                >
                  <span className={iconClasses(location.pathname.startsWith('/manajemen-konten'))}>
                    <FiFileText />
                  </span>
                  <p className="truncate">Manajemen Konten</p>
                </Link>
              )}
            </div>
          </nav>
          <div className="border-t border-orange-400 px-4 py-4">
            <div className="flex items-center justify-center rounded-xl border border-orange-400 bg-orange-50 px-3 py-3">
              <span className="mr-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-orange-600">
                By : 
              </span>
              <img
                src="/logo_dpudt.png"
                alt="Logo DPU DT"
                className="h-7 w-auto object-contain"
              />
            </div>
          </div>
        </div>
      </aside>

      <div
        className={`min-h-screen bg-slate-50 pt-16 transition-all duration-300 ${
          isOpen ? 'pl-72' : 'pl-4'
        }`}
      >
        {children}
      </div>
    </>
  );
}

export default SidebarAdmin;